import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import createPersistedState from 'vuex-persistedstate';
import { get, isNil } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { createImageUploadFormData } from '@/utils/imageUpload';
import { clearPersistedUserLocale, persistUserLocale } from '@/utils/locale.js';
import EventBus from './eventBus';

Vue.use(Vuex);

const SET_THREADS = 'SET_THREADS';

const PlaceholderPlugin = (store) => {
    // called when the store is initialized
    store.subscribe((mutation, state) => {
        // called after every mutation.
        // The mutation comes in the format of `{ type, payload }`.
    });
};

// isLocalStorage checks if local storage available to detect wheather it is CNA popup or not
function isLocalStorage() {
    const test = 'test';
    try {
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
        // available
    } catch (e) {
        return false;
        // unavailable
    }
}

function castLimitConfigValue(value) {
    if (typeof value !== 'string') {
        return value;
    }

    const normalizedValue = value.trim();
    const lowercaseValue = normalizedValue.toLowerCase();

    if (lowercaseValue === 'true') {
        return true;
    }

    if (lowercaseValue === 'false') {
        return false;
    }

    if (normalizedValue !== '' && !Number.isNaN(Number(normalizedValue))) {
        return Number(normalizedValue);
    }

    return value;
}

function normalizeLimits(limits = {}) {
    const config = get(limits, 'Config', {});
    const parsedConfig = Object.keys(config).reduce(
        (normalizedConfig, key) => ({
            ...normalizedConfig,
            [key]: castLimitConfigValue(config[key]),
        }),
        {}
    );

    // The all_on toggle enables the premium config flags and expands related limits.
    if (parsedConfig.all_on === true) {
        Object.assign(parsedConfig, {
            application_fee: true,
            customization: true,
            // Permission management follows the same all-features override as other premium flags.
            manage_permissions: true,
            max_custom_field: 100,
            pin_post: true,
            vanity_domain: true,
            white_label: true,
        });
    }

    return {
        SpaceID: get(limits, 'SpaceID', ''),
        MaxUsers: get(limits, 'MaxUsers', 0),
        MaxAdmins: parsedConfig.all_on === true ? 100 : get(limits, 'MaxAdmins', 0),
        MaxResources: parsedConfig.all_on === true ? 100 : get(limits, 'MaxResources', 0),
        MaxChannels: parsedConfig.all_on === true ? 100 : get(limits, 'MaxChannels', 0),
        MaxEvents: get(limits, 'MaxEvents', 0),
        MaxPages: get(limits, 'MaxPages', 0),
        UserThresholdReached: get(limits, 'UserThresholdReached', false),
        UserLimitReached: get(limits, 'UserLimitReached', false),
        // Cast string config values from the API into booleans or numbers where possible.
        Config: parsedConfig,
    };
}

let presistant = PlaceholderPlugin;
if (isLocalStorage()) {
    try {
        presistant = createPersistedState();
    } catch (error) {
        // Failed to initialize vuex-persistedstate; continue with the default PlaceholderPlugin.
        // eslint-disable-next-line no-console
        console.error('Failed to initialize vuex-persistedstate:', error);
    }
}

export default new Vuex.Store({
    plugins: [presistant],
    state: {
        isLocalStorage: false,
        isInMobile: false,
        me: { Profile: { Initials: 'NA' } },
        notificationsNumber: 0,
        // Pending agreement tasks count used by notification badges.
        tasksNumber: 0,
        projectToEdit: null,
        space: {},
        eventDuplicate: null,
        // Cached space legal agreements fetched once on app load.
        privacyPolicy: null,
        termsAndConditions: null,
        privacyPolicyLoaded: false,
        termsAndConditionsLoaded: false,

        // new membership
        newMembership: {
            Step: 0, // 0 select plan, 1 plan upgrades, 2 account, 3 address, 4 payment, 5 review
            Plan: null,
            Extras: [],
            Account: null,
            Address: null,
            CustomerID: '',
            PaymentMethodeID: '',
            ClientSecret: '',
            ExpriresAt: 0,
            OA2UserData: null,
            IsFromOA2Provider: false,
            InviteID: '',
        },
        threads: null,
        newMessages: null,

        billingTurnedOn: false,
        circlesOn: false,
        // Cached custom menu label overrides from the Unleash variant payload.
        customMenuLabelOverrides: {},

        breadcrumbs: [],

        invite: null, // invite object with prepopulated data

        permissions: {},

        intercomEventQueue: [],

        welcome_checklist: {},

        limits: {
            Config: {},
        }, // community limits and quoats. e.g. max number of users

        plans: [], // list of available plans in community if any
        postEditorDrafts: {}, // persisted via vuex-persistedstate
        tablePreferences: {}, // persisted table-specific UI preferences keyed by table id
        postCreationNudgeDismissals: {}, // persisted dismissal timestamps keyed by user id
        auth: {},
    },
    /* eslint-disable no-param-reassign */
    mutations: {
        SET_PLANS(state, plans) {
            state.plans = plans;
        },
        SET_POST_EDITOR_DRAFT(state, payload) {
            const { key, draft } = payload || {};
            if (!key) return;
            Vue.set(state.postEditorDrafts, key, draft);
        },
        CLEAR_POST_EDITOR_DRAFT(state, key) {
            if (!key || !state.postEditorDrafts || !state.postEditorDrafts[key]) return;
            Vue.delete(state.postEditorDrafts, key);
        },
        SET_POST_CREATION_NUDGE_DISMISSED_AT(state, payload) {
            const { userID, dismissedAt } = payload || {};
            if (!userID || !dismissedAt) return;
            Vue.set(state.postCreationNudgeDismissals, String(userID), dismissedAt);
        },
        SET_TABLE_PREFERENCE(state, payload) {
            const { tableKey, preferenceKey, value } = payload || {};
            if (!tableKey || !preferenceKey) return;

            const currentPreferences = state.tablePreferences[tableKey] || {};
            Vue.set(state.tablePreferences, tableKey, {
                ...currentPreferences,
                [preferenceKey]: value,
            });
        },
        CLEAR_TABLE_PREFERENCE(state, payload) {
            const { tableKey, preferenceKey } = payload || {};
            if (!tableKey || !state.tablePreferences[tableKey]) return;

            if (!preferenceKey) {
                Vue.delete(state.tablePreferences, tableKey);
                return;
            }

            const nextPreferences = {
                ...state.tablePreferences[tableKey],
            };
            delete nextPreferences[preferenceKey];

            if (Object.keys(nextPreferences).length === 0) {
                Vue.delete(state.tablePreferences, tableKey);
                return;
            }

            Vue.set(state.tablePreferences, tableKey, nextPreferences);
        },
        SET_AUTH_STATE(state, payload) {
            state.auth = {
                ...state.auth,
                ...payload,
            };
        },
        SET_LIMITS(state, limits) {
            state.limits = normalizeLimits(limits);
        },
        // Stores the space privacy policy fetched on app load.
        SET_PRIVACY_POLICY(state, policy) {
            state.privacyPolicy = policy;
        },
        // Stores the space terms and conditions fetched on app load.
        SET_TERMS_AND_CONDITIONS(state, terms) {
            state.termsAndConditions = terms;
        },
        // Marks that the privacy policy request has completed.
        SET_PRIVACY_POLICY_LOADED(state, loaded) {
            state.privacyPolicyLoaded = loaded;
        },
        // Marks that the terms and conditions request has completed.
        SET_TERMS_AND_CONDITIONS_LOADED(state, loaded) {
            state.termsAndConditionsLoaded = loaded;
        },
        WELCOME_CHECKLIST(state, checklist) {
            state.welcome_checklist = checklist;
        },
        SET_PERMISSIONS(state, permissions) {
            state.permissions = permissions;
        },
        // Set isInMobile state
        SET_IS_IN_MOBILE(state, value) {
            state.isInMobile = value;
        },
        // Set mobileAppVersion state
        SET_MOBILE_APP_VERSION(state, version) {
            state.mobileAppVersion = version;
        },

        SET_SPACE_OBJECT_FOR_ADMIN(state, adminspaceobject) {
            state.space_configuration_admin = adminspaceobject;
        },
        SET_BREADCRUMBS(state, breadcrumbs) {
            state.breadcrumbs = breadcrumbs;
        },
        SET_CIRCLES_ON(state, value) {
            state.circlesOn = value;
        },
        // Store custom menu label override values while keeping the object reactive.
        SET_CUSTOM_MENU_LABEL_OVERRIDES(state, customMenuLabelOverrides) {
            Vue.set(state, 'customMenuLabelOverrides', { ...(customMenuLabelOverrides || {}) });
        },
        // new membership mutations
        SET_INVITE_ID(state, id) {
            state.newMembership.InviteID = id;
        },
        SET_INVITE(state, invite) {
            state.invite = invite;
        },
        RESET_MEMBERSHIP_PROVIDER(state) {
            state.newMembership.IsFromOA2Provider = false;
            state.newMembership.OA2UserData = null;
        },
        SET_NEW_MEMBERSHIP_ACCOUNT_DATA(state, userData) {
            state.newMembership.IsFromOA2Provider = true;
            state.newMembership.OA2UserData = {
                FirstName: userData.FirstName,
                LastName: userData.LastName,
                Email: userData.Email,
            };
        },
        SET_COUPON(state, coupon) {
            state.newMembership.Coupon = coupon.Coupon;
        },
        RESET_NEW_MEMBERSHIP(state) {
            state.newMembership = {
                ...state.newMembership,
                Step: 0,
                Plan: null,
                Extras: [],
                Account: null,
                Address: null,
                CustomerID: '',
                PaymentMethodeID: '',
                ClientSecret: '',
                ExpriresAt: 0,
                Coupon: null,
            };
            if (state.newMembership.IsFromOA2Provider) {
                state.newMembership.Account = state.newMembership.OA2UserData;
            }
        },
        SET_STEP(state, step) {
            state.newMembership.Step = step;
        },
        PREVIOUS_STEP(state) {
            if (state.newMembership.Step === 3 && state.newMembership.IsFromOA2Provider) {
                if (get(state, 'newMembership.Plan.Extras', []).length === 0) {
                    state.newMembership.Step = 0;
                } else {
                    state.newMembership.Step = 1;
                }
                return;
            }
            if (state.newMembership.Step !== 0) {
                state.newMembership.Step -= 1;
            }
        },
        SET_PLAN(state, plan) {
            state.newMembership.Plan = plan;
            state.newMembership.Extras = [];
            const now = Math.round(new Date().getTime() / 1000);
            const later = now + 60 * 60;
            state.newMembership.ExpriresAt = later;
            state.newMembership.Step = 1;
        },
        SET_EXTRAS(state, extras) {
            state.newMembership.Extras = extras;
        },
        SAVE_EXTRAS(state, extras) {
            state.newMembership.Extras = extras;
            const now = Math.round(new Date().getTime() / 1000);
            const later = now + 60 * 60;
            state.newMembership.ExpriresAt = later;
            if (state.newMembership.IsFromOA2Provider) {
                state.newMembership.Step = 3;
            } else {
                state.newMembership.Step = 2;
            }
        },
        SET_ACCOUNT(state, account) {
            state.newMembership.Account = account;
            const now = Math.round(new Date().getTime() / 1000);
            const later = now + 60 * 60;
            state.newMembership.ExpriresAt = later;
            state.newMembership.Step = 3;
        },

        SET_ADDRESS(state, address) {
            state.newMembership.Address = address;
            const now = Math.round(new Date().getTime() / 1000);
            const later = now + 60 * 60;
            state.newMembership.ExpriresAt = later;
            state.newMembership.Step = 4;
        },

        SAVE_CUSTOMER_AND_CLIENT_SECRET(state, data) {
            state.newMembership.CustomerID = data.CustomerID;
            state.newMembership.ClientSecret = data.ClientSecret;
        },
        SET_PAYMENT_METHOD_ID(state, id) {
            state.newMembership.PaymentMethodeID = id;
        },
        CLEAR_CUSTOMER_AND_CLINENT_SECRET(state) {
            state.newMembership.CustomerID = null;
            state.newMembership.ClientSecret = null;
            state.newMembership.PaymentMethodeID = null;
        },
        TO_REVIEW(state) {
            const now = Math.round(new Date().getTime() / 1000);
            const later = now + 60 * 60;
            state.newMembership.ExpriresAt = later;
            state.newMembership.Step = 5;
        },

        SET_NEW_NOTIFICATIONS(state, n) {
            state.notificationsNumber = n;
            EventBus.$emit('NEWNOTIFICATIONS', n);
        },
        SET_TASKS_COUNT(state, n) {
            state.tasksNumber = n;
            EventBus.$emit('NEWTASKS', n);
        },
        DECREMENT_UNSEEN_NOTIFICATIONS(state) {
            if (state.notificationsNumber > 0) {
                state.notificationsNumber--;
            }
        },

        LOCALSTORAGE_TRUE(state) {
            state.isLocalStorage = true;
        },
        LOGOUT(state) {
            if (state.isLocalStorage) {
                window.localStorage.removeItem('vuex');
            }
            clearPersistedUserLocale();
            state.auth = {};
            state.me = { Profile: { Initials: 'NA' } };
        },
        GETME(state, me) {
            if (me && me.Language) {
                // Cache the user locale so the next full reload starts in the correct language.
                persistUserLocale(me.Language);
            }
            state.me = me;
        },
        GETSPACE(state, space) {
            state.space = space;
        },

        HANDLE_REALTIME_CHAT_MESSAGE(state, message) {
            if (message && message.thread_id != null) {
                if (!state.newMessages) {
                    state.newMessages = { TotalNumber: 0, MessagesPerThread: {} };
                }
                // Vue 2 needs $set so badges react when this thread has no prior unread messages.
                if (!state.newMessages.MessagesPerThread) {
                    Vue.set(state.newMessages, 'MessagesPerThread', {});
                }
                const threadID = message.thread_id;
                Vue.set(
                    state.newMessages.MessagesPerThread,
                    threadID,
                    (state.newMessages.MessagesPerThread[threadID] || 0) + 1
                );
                // Keep the navigation badge aligned with the per-thread unread counters.
                state.newMessages.TotalNumber = (state.newMessages.TotalNumber || 0) + 1;
                EventBus.$emit(`MESSAGE_${message.thread_id}`, message);
                EventBus.$emit('THREAD_ACTIVITY', message);
                EventBus.$emit(`MESSAGETOAST`, message);
                EventBus.$emit('NEWMESSAGES', state.newMessages.TotalNumber);
            }
        },
        SET_THREADS(state, threads) {
            state.threads = threads;
        },
        SET_THREAD(state, thread, key) {
            state.threads[key] = thread;
        },

        SET_NEW_MESSAGES_NUMBERS(state, data) {
            state.newMessages = data;
            EventBus.$emit('NEWMESSAGES', get(data, 'TotalNumber', 0));
        },
        DECREMENT_UNSEEN_MESSAGES(state, threadID) {
            if (state.newMessages != null && state.newMessages.TotalNumber && state.newMessages.TotalNumber != 0) {
                state.newMessages.TotalNumber--;
            }

            if (
                state.newMessages != null &&
                state.newMessages.MessagesPerThread &&
                state.newMessages.MessagesPerThread[threadID] != 0
            ) {
                state.newMessages.MessagesPerThread[threadID]--;
            }
            EventBus.$emit('NEWMESSAGES', get(state, 'newMessages.TotalNumber', 0));
        },

        SET_PAYMENT_READY_STATE(state, value) {
            state.billingTurnedOn = value;
        },
        // intercom event qeue
        ADD_TO_INTERCOM_QEUE(state, value) {
            state.intercomEventQueue.push({
                id: uuidv4(),
                data: value,
            });
        },
        REMOVE_FROM_INTERCOM_QEUE(state, value) {
            if (!value) return;
            state.intercomEventQueue = state.intercomEventQueue.filter((item) => item.id !== value.id);
        },
    },
    /* eslint-disable no-param-reassign */

    actions: {
        /**
         *
         * @param {*} param0
         * @returns
         */
        getDashboardURL({ _ }, dashboardNumber) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/admin/statistics?dashboard=${dashboardNumber}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data.DashboardURL);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        getTablePreference({ state }, payload) {
            const { tableKey, preferenceKey, fallback = null } = payload || {};
            if (!tableKey || !preferenceKey) return fallback;

            return get(state.tablePreferences, `${tableKey}.${preferenceKey}`, fallback);
        },
        setTablePreference({ commit }, payload) {
            commit('SET_TABLE_PREFERENCE', payload);
        },
        clearTablePreference({ commit }, payload) {
            commit('CLEAR_TABLE_PREFERENCE', payload);
        },
        getOpenGraph({ commit }, url) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/dashboard/og?url=${url}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        getNewMessagesNumbers({ commit }) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: '/chat/new',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        if (response.data) {
                            commit('SET_NEW_MESSAGES_NUMBERS', response.data);
                        }
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        // spaces
        getSpaceSettings({ commit }) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: '/space/settings',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        commit('GETSPACE', response.data);
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        // Fetch space legal agreements once and keep them in the store for reuse.
        getLegalAgreements({ commit, state }) {
            const requests = [];

            requests.push(
                axios({
                    method: 'GET',
                    url: '/space/get-privacy-policy',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        commit('SET_PRIVACY_POLICY', get(response, 'data', null));
                    })
                    .catch((error) => {
                        commit('SET_PRIVACY_POLICY', null);
                        // eslint-disable-next-line no-console
                        console.error('Failed to fetch privacy policy:', error);
                    })
                    .finally(() => {
                        commit('SET_PRIVACY_POLICY_LOADED', true);
                    })
            );

            requests.push(
                axios({
                    method: 'GET',
                    url: '/space/get-terms-and-conditions',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        commit('SET_TERMS_AND_CONDITIONS', get(response, 'data', null));
                    })
                    .catch((error) => {
                        commit('SET_TERMS_AND_CONDITIONS', null);
                        // eslint-disable-next-line no-console
                        console.error('Failed to fetch terms and conditions:', error);
                    })
                    .finally(() => {
                        commit('SET_TERMS_AND_CONDITIONS_LOADED', true);
                    })
            );

            return Promise.all(requests);
        },

        // getLimits fetches community limitations and quotas
        getLimits({ commit }) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: '/space/limits',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        commit('SET_LIMITS', response.data);
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        getWelcomeChecklist({ commit }) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: '/admin/space/welcome-checklist',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        commit('WELCOME_CHECKLIST', response.data);
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        // actions for projects
        getProjectsByNewest({ commit }, next) {
            let url = '/project/list/newest';
            if (next) {
                url = next;
            }
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        // actions for profiles
        getCommunity({ commit }, next) {
            let url = '/user/profile/list';
            if (next) {
                url = next;
            }
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        // actions for Comment
        deleteComment({ commit }, project) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: '/comment',
                    data: project,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        deleteCommentAdmin({ commit }, project) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: '/admin/comment',
                    data: project,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        createComment({ commit }, object) {
            const data = JSON.stringify(object);
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/comment',
                    data,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        listCommentsForPost({ commit }, url) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url, // todo make comments pagination
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        // actions for Posts(ShoutOut)
        getPostByID({ commit }, id) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/post/by-id/${id}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        deletePost({ commit }, project) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: '/post',
                    data: project,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        deletePostAdmin({ commit }, project) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: '/admin/post',
                    data: project,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        getPostBySlug({ commit }, slug) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/post/by-slug/${slug}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        createPost({ commit }, object) {
            const data = JSON.stringify(object);
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/post',
                    data,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        updatePost({ commit }, object) {
            const data = JSON.stringify(object);
            return new Promise((resolve, reject) => {
                axios({
                    method: 'PUT',
                    url: '/post',
                    data,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        addEmoji({ commit }, reaction) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/v1/reactions',
                    data: reaction,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        deleteEmoji({ commit }, reaction) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: '/v1/reactions',
                    data: reaction,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        // action for events
        createEvent({ commit }, object) {
            const data = JSON.stringify(object);
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/event',
                    data,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        updateEvent({ commit }, object) {
            const data = JSON.stringify(object);
            return new Promise((resolve, reject) => {
                axios({
                    method: 'PUT',
                    url: '/event',
                    data,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        adminUpdateEvent({ commit }, object) {
            const data = JSON.stringify(object);
            return new Promise((resolve, reject) => {
                axios({
                    method: 'PUT',
                    url: '/admin/event',
                    data,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        /**
         * getEventBySlug return event for given slug name
         * @param {*} param0
         * @param {*} slug
         */
        getEventBySlug({ commit }, slug) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/event/by-slug/${slug}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        // getevents v2
        getEventsV2({ commit }, next) {
            let url = '/event/list';
            if (next) {
                url = next;
            }
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        searchMembers(_, input) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/search/user/${input.searchText}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    cancelToken: input.cancelToken,
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        adminSearchMembers(_, input) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/admin/member/search/${input.searchText}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    cancelToken: input.cancelToken,
                })
                    .then((response) => {
                        resolve(response.data.objects);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        addParticipant({ commit }, request) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/event/participant',
                    data: request,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        removeParticipant({ commit }, request) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: '/event/participant',
                    data: request,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        addOrganizer({ commit }, request) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/admin/event/organizer',
                    data: request,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        removeOrganizer({ commit }, request) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: '/admin/event/organizer',
                    data: request,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        // get past events
        getPastEvents({ commit }, next) {
            let url = '/event/list/past';
            if (next) {
                url = next;
            }
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        // getevents
        getEvents({ commit }) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: '/event/list',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        // Actions for events
        listEventTypes({ commit }, path) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/admin/event/type/list?${path}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        createEventType({ commit }, name) {
            const data = JSON.stringify({ Value: name });
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/admin/event/type',
                    data,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        // actions for plans

        editPlan({ commit }, object) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'PUT',
                    url: '/admin/plan/update',
                    data: object,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        getPlanByID({ commit }, ID) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/admin/plan/${ID}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        // Actions for Time Passes

        editTimepass({ commit }, object) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'PUT',
                    url: '/admin/time-pass',
                    data: object,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        createTimepass({ commit }, object) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/admin/time-pass',
                    data: object,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        deleteTimepass({ commit }, object) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: 'admin/time-pass',
                    data: object,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        // Actions for Tags
        /**
         * getTagsByIDs - return tags for given ids
         * @param {*} param0
         * @param {*} IDs {"IDS": [strings]}
         */
        getTagsByIDs({ commit }, IDs) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/tags/listbyids',
                    data: IDs,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        /**
         * createTag - creates tag for input vaue
         * @param {"Value": "string"}
         */
        createTag({ commit }, tag) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/tags',
                    data: tag,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        /**
         * searchTag - returns search result and autocomplete suggestions for input
         * @param queryparam term
         */
        searchTag({ commit }, term) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/tags/search?term=${term}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        // Notifications methodes
        setNotificationAsSeen({ commit }, notification) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/notification',
                    data: notification,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        getGetNewNotifications({ commit }) {
            return new Promise(async (resolve, reject) => {
                let notifications = {};
                let notificationsCount = 0;
                let agreements = {};
                let agreementsCount = 0;

                try {
                    notifications = await axios.get('/notification/new');
                } catch (err) {}

                try {
                    agreements = await axios.get('/user/new-agreements');
                } catch (err) {}

                notificationsCount = get(notifications, 'data.Notifications.length', 0);
                agreementsCount = get(agreements, 'data.length', 0);

                commit('SET_NEW_NOTIFICATIONS', notificationsCount);
                commit('SET_TASKS_COUNT', agreementsCount);
                resolve(get(notifications, 'data', {}));
            });
        },

        getAllNotifications({ commit }, next) {
            let url = '/notification/list';
            if (next) {
                url = next;
            }
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        getMyProjects({ commit }) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: '/project/my',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        createBookingV2({ commit }, booking) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/booking/resources',
                    data: booking,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        cancelBookingV2({ commit }, bookingid) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: `/booking/${bookingid}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        getSlots({ commit }, requestparams) {
            const { resourceID } = requestparams;
            delete requestparams.resourceID;
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    params: requestparams,
                    url: `/booking/resources/${resourceID}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        /*
     getResources return list of bookable resources sorted by spaces(booking groups)
     for given resource type `machines` or `formeeting`
   */

        getResources({ commit }, type) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/booking/resources/${type}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        /**
         *
         * @param {*} param0
         * @param {*} owner
         */
        addOwner({ commit }, owner) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'PUT',
                    url: '/project/owners',
                    data: owner,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        addContributor({ commit }, contributor) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'PUT',
                    url: '/project/contributors',
                    data: contributor,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        removeOwner({ commit }, owner) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: '/project/owners',
                    data: owner,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        removeContributor({ commit }, contributor) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: '/project/contributors',
                    data: contributor,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        deleteProject({ commit }, project) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: '/project',
                    data: project,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        deleteProjectAdmin({ commit }, project) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: '/admin/project',
                    data: project,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        updateProject({ commit }, project) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'PUT',
                    url: '/project',
                    data: project,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        /*
    listProjectsByIDs reqiests list of Projects for given id list from backend
    :param IDs should have format below

    {
      "IDS" :[
      {
        "ID": "testid"
      },
      {
        "ID": "testid2"
      },
      {
        "ID": "testi3"
      }
      ]
    }
    */
        listProjectsByIDs({ commit }, IDs) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/project/listbyids',
                    data: IDs.ids,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        getProjectBySlug({ commit }, slug) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/project/by-slug/${slug}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        /*
    listUsersByIDs reqiests list of users for given id list from backend
    :param IDs should have format below

    {
      "IDS" :[
      {
        "ID": "testid"
      },
      {
        "ID": "testid2"
      },
      {
        "ID": "testi3"
      }
      ]
    }
    */
        deleteJobAdmin({ commit }, job) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: '/admin/job',
                    data: job,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        listUsersByIDs({ commit }, IDs) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/user/listbyids',
                    data: IDs.ids,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        getUserBySlug({ commit }, slug) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/user/profile/by-slug/${slug}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        detachPaymentMethod({ commit }, methodID) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: `/user/account/billing/delete-payment-method`,
                    data: JSON.stringify({ ID: methodID }),
                    timeout: 5000,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        detachPaymentMethodAdmin({ commit }, body) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'DELETE',
                    url: `/admin/payment/stripe/payment-methods/${body.userId}/${body.methodID}`,
                    data: JSON.stringify({ ID: body.methodID }),
                    timeout: 5000,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        adminGetUserByID({ commit }, userID) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/admin/community/member/${userID}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        /*
      imageUpload uplodes image to s3 backed through gateway
      :param file should be an image file fro input type file
      :param url it is a path which describes in which folder on s3 will be hosted file
      url example /upload/image/projects-headers =>
      'projects-headers'  here is a name for folder, if folder does not exist, it will be created
      ':response return a json with url inside with key url
    */
        imageUpload({ commit }, kwargs) {
            return createImageUploadFormData(kwargs.file)
                .then((data) =>
                    axios({
                        method: 'POST',
                        url: kwargs.url,
                        data,
                        withCredentials: true,
                    })
                )
                .then((response) => response.data.url);
        },
        updateUser({ commit }, userForm) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'PUT',
                    url: '/me',
                    withCredentials: true,
                    data: userForm,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        const string = response.data.Profile.Name.split(' ');
                        let initials = '';
                        for (let i = 0; i < string.length; i++) {
                            initials += string[i][0];
                        }
                        response.data.Profile.Initials = initials;
                        commit('GETME', response.data);
                        resolve(response);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        adminDeleteUser({ commit }, ID) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'PUT',
                    url: `/admin/community/member/delete/${ID}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        updateEmail({ commit }, userForm) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/user/set-new-email',
                    withCredentials: true,
                    data: userForm,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        adminUpdateOtherUser({ commit }, userForm) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'PUT',
                    url: '/admin/community/member/v2/',
                    withCredentials: true,
                    data: userForm,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        searchUser({ commit }, input) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/search/user/${input.input}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        async logout({ commit }) {
            try {
                // The BFF owns the HttpOnly session cookie; the browser includes it automatically.
                await axios.post(`${window.location.origin}/bff/logout`, null, { withCredentials: true });
            } catch (error) {
                // Clearing the client state is still the safest fallback during logout.
            } finally {
                commit('LOGOUT');
            }
        },

        async getMe({ commit }) {
            const response = await axios({
                method: 'GET',
                url: '/me',
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            commit('GETME', response.data);
            return {
                data: response.data,
            };
        },
        getSignupSettings({ commit }) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: '/space/signup-settings',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        // Keep the legacy simple signup flow working for the public /signup route.
        signup(_, data) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/user/signup',
                    withCredentials: true,
                    data,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        validateSignupInvite({ commit }, token) {
            const data = {
                InviteID: token,
            };
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: `/user/invite/validate`,
                    withCredentials: true,
                    data,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        if (response.data) {
                            commit('SET_INVITE', response.data);
                            resolve(response.data);
                        } else {
                            commit('SET_INVITE', null);
                            reject(response);
                        }
                    })
                    .catch((error) => {
                        commit('SET_INVITE', null);
                        reject(error);
                    });
            });
        },

        getInvitedUsers({ _ }, url) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        getPaymentMethods() {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: '/user/account/billing/payment-methods',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        adminGetPaymentMethods({ commit }, data) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/admin/payment/stripe/payment-methods/${data.ID}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        listLocations() {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: '/admin/location/list',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data.Locations);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        listZapierApiKeys() {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: '/admin/zapier/api-key/list',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        createZapierApiKey() {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/admin/zapier/api-key',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        fetchAuthMethods() {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: '/auth-method/',
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        getAuthMethodURL(_, methodID) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'GET',
                    url: `/auth-method/${methodID}`,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then((response) => {
                        resolve(get(response, 'data.url'));
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
        authenticateWithOA2Provider({ commit }, body) {
            return new Promise((resolve, reject) => {
                axios({
                    method: 'POST',
                    url: '/auth-method/authenticate',
                    data: body,
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })
                    .then(async (response) => {
                        if (!isNil(get(response, 'data.userData', null))) {
                            commit('SET_NEW_MEMBERSHIP_ACCOUNT_DATA', response.data.userData);
                            resolve({
                                isLogin: false,
                            });
                            return;
                        }

                        const token = get(response, 'data.token', null);
                        if (token) {
                            // Exchange the legacy OAuth JWT immediately without persisting it in the browser.
                            const bootstrapResponse = await fetch('/bff/login/bootstrap', {
                                body: JSON.stringify({ access_token: token }),
                                credentials: 'include',
                                headers: { 'Content-Type': 'application/json' },
                                method: 'POST',
                            });
                            if (!bootstrapResponse.ok) {
                                throw new Error(`Unable to bootstrap OAuth session (${bootstrapResponse.status})`);
                            }
                        }

                        const sessionResponse = await fetch('/bff/session', { credentials: 'include' });
                        if (!sessionResponse.ok) {
                            throw new Error(`Unable to restore OAuth session (${sessionResponse.status})`);
                        }
                        const session = await sessionResponse.json();
                        if (!session.authenticated) {
                            throw new Error('OAuth exchange did not create an authenticated session');
                        }
                        commit('SET_AUTH_STATE', { authenticated: true, session });

                        resolve({
                            isLogin: true,
                        });
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },
    },
    getters: {
        authState: (state) => state.auth,
        state: (state) => state,
        // BFF session restoration is the single source of truth for route access.
        isAuthenticated: (state) => Boolean(state.auth && state.auth.authenticated),
    },
});
